import { GroupByMode } from '@griply/types';
import {
  filterGoals,
  getGoalListOrder,
  getLifeAreaGoalFilterDocument,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useUpdateLifeArea } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { LifeArea } from 'shared/types/life-area';
import { GoalSortingMode, OrderDirection } from 'shared/types/sorting-mode';
import {
  GoalFilterList,
  SortBar,
  useGoalsByFilterDocument,
} from 'shared/views/goal-filter-list';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

import { useLifeAreaGoalReorder } from '../../hooks/use-life-area-goal-reorder';
import { LifeAreaRoadmapEmptyState } from '../life-area-roadmap-empty-state';
import * as Styled from './life-area-roadmap-overview.style';

const excludedContextMenuOptions = [ContextMenuOptions.ShowRoadmap];

type LifeAreaRoadmapOverviewProps = {
  lifeArea: LifeArea;
  scrollElement?: React.RefObject<HTMLDivElement>;
  hideCompleted?: boolean;
  hideArchived?: boolean;
  fullSize?: boolean;
};

export const LifeAreaRoadmapOverview: React.FC<
  LifeAreaRoadmapOverviewProps
> = ({ lifeArea, scrollElement, hideCompleted, hideArchived, fullSize }) => {
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const allGoals = useSortedGoals();
  const links = useEntityLinks();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const taskCounts = useTaskCounts();

  const sortMode = lifeArea.goalSortingMode ?? GoalSortingMode.Custom;
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;

  const onSortChange = useCallback(
    (mode: GoalSortingMode) => {
      updateLifeArea({ id: lifeArea.id, goalSortingMode: mode });
      updateUserSettings({ goalSortDirection: OrderDirection.Asc });
    },
    [lifeArea.id, updateLifeArea, updateUserSettings],
  );

  const onSortDirectionToggle = useCallback(() => {
    updateUserSettings({
      goalSortDirection:
        sortDirection === OrderDirection.Asc
          ? OrderDirection.Desc
          : OrderDirection.Asc,
    });
  }, [sortDirection, updateUserSettings]);

  const onSectionCollapse = useCallback(
    (newCollapsedSections: string[]) => {
      updateLifeArea({ id: lifeArea.id, collapsedGoals: newCollapsedSections });
    },
    [lifeArea.id, updateLifeArea],
  );

  const filterDocument = useMemo(
    () => getLifeAreaGoalFilterDocument(lifeArea, allGoals),
    [allGoals, lifeArea],
  );

  // No goalTypeFilter: a roadmap holds the life area's goals and its projects.
  const roadmapGoals = useGoalsByFilterDocument(filterDocument, {
    customOrder: undefined,
  });

  const customOrder = useMemo(
    () =>
      getGoalListOrder({
        goals: roadmapGoals,
        allGoals,
        sortMode,
        sortDirection,
        goalSorting: lifeArea.goalSorting,
        taskCounts: taskCounts?.byGoalId,
        goalProgressCounts: taskCounts?.byGoalProgress,
      }),
    [
      roadmapGoals,
      allGoals,
      sortMode,
      sortDirection,
      lifeArea.goalSorting,
      taskCounts?.byGoalId,
      taskCounts?.byGoalProgress,
    ],
  );

  const rootLink = useMemo(() => ({ lifeAreaId: lifeArea.id }), [lifeArea.id]);

  const onReorder = useLifeAreaGoalReorder(lifeArea.id);

  if (
    !filterGoals(roadmapGoals, { links, hideArchived, hideCompleted }).length
  ) {
    return <LifeAreaRoadmapEmptyState lifeAreaId={lifeArea.id} />;
  }

  return (
    <Styled.ScrollContainer ref={scrollContainerRef} $scrollAuto={!!fullSize}>
      <SortBar
        value={sortMode}
        direction={sortDirection}
        onChange={onSortChange}
        onDirectionToggle={onSortDirectionToggle}
        hideLifeArea
      />
      <GoalFilterList
        rootLink={rootLink}
        filterDocument={filterDocument}
        customOrder={customOrder}
        onSectionCollapse={onSectionCollapse}
        scrollElement={
          fullSize ? scrollContainerRef : scrollElement ?? scrollContainerRef
        }
        hideCompleted={hideCompleted}
        hideArchived={hideArchived}
        onReorder={onReorder}
        excludedContextMenuOptions={excludedContextMenuOptions}
        hideLifeArea
        showProjectParent
        hideProjectLifeAreaLabel
        nestSubGoals={filterDocument.groupByMode !== GroupByMode.Deadline}
      />
    </Styled.ScrollContainer>
  );
};
